import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

/**
 * MagneticCursor — site-wide custom pointer.
 *  - 40px ring + 4px dot, mix-blend-mode: difference
 *  - Scales 2.2× and shifts to coral when hovering interactable elements
 *  - Hides on touch devices
 */
export default function MagneticCursor() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { damping: 30, stiffness: 350, mass: 0.6 });
  const sy = useSpring(y, { damping: 30, stiffness: 350, mass: 0.6 });
  const [variant, setVariant] = useState('default'); // 'default' | 'hover'
  const [hidden, setHidden] = useState(true);
  const lastMove = useRef(0);

  useEffect(() => {
    if ('ontouchstart' in window) return; // skip touch devices
    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (hidden) setHidden(false);
      lastMove.current = Date.now();
    };
    const onOver = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const interactable = t.closest('a, button, [role="button"], input, textarea, [data-magnetic]');
      setVariant(interactable ? 'hover' : 'default');
    };
    const onLeave = () => setHidden(true);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);
    window.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, [hidden, x, y]);

  if ('ontouchstart' in window) return null;

  return (
    <>
      {/* Outer ring */}
      <motion.div
        aria-hidden
        style={{
          x: sx, y: sy,
          translateX: '-50%', translateY: '-50%',
          opacity: hidden ? 0 : 1,
        }}
        animate={{
          width: variant === 'hover' ? 64 : 40,
          height: variant === 'hover' ? 64 : 40,
          borderColor: variant === 'hover' ? 'rgba(255,107,74,0.85)' : 'rgba(255,255,255,0.5)',
          backgroundColor: variant === 'hover' ? 'rgba(255,107,74,0.08)' : 'rgba(255,255,255,0)',
        }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="fixed top-0 left-0 rounded-full border pointer-events-none z-[9999] mix-blend-difference will-change-transform"
      />
      {/* Inner dot */}
      <motion.div
        aria-hidden
        style={{
          x: sx, y: sy,
          translateX: '-50%', translateY: '-50%',
          opacity: hidden ? 0 : 1,
        }}
        animate={{
          width: variant === 'hover' ? 6 : 4,
          height: variant === 'hover' ? 6 : 4,
          backgroundColor: variant === 'hover' ? '#FF6B4A' : '#FFFFFF',
        }}
        transition={{ duration: 0.12 }}
        className="fixed top-0 left-0 rounded-full pointer-events-none z-[9999] will-change-transform"
      />
      <style>{`html, body, button, a { cursor: none !important; } @media (hover: none) { html, body, button, a { cursor: auto !important; } }`}</style>
    </>
  );
}
